/* eslint-disable no-restricted-imports */
import React from 'react';
import { Button, Grid } from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  hasCatalogProcessingErrors,
  isOrphan,
  isKind,
} from '@backstage/plugin-catalog';
import {
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityUserProfileCard } from './User/EntityUserProfileCard';
import {
  isAWSCostAnnotationPresent,
  AwsMiniCostCenterPage,
} from '@ncgt/plugin-aws-mini-cost-center';
import {
  isAWSS3ViewerAnnotationPresent,
  AwsS3BucketViewerPage,
} from '@ncgt/plugin-aws-s3-bucket-viewer';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  Entity,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  EntityAwsCodeBuildCard,
  isAwsCodeBuildAvailable,
} from '@aws/aws-codebuild-plugin-for-backstage';

import {
  ComponentStatsCard,
  TeamStructureCard,
} from '@internal/backstage-plugin-github-statistics';
import { CXOneCard } from '@internal/backstage-plugin-cxone';
import { TeamInfoCard } from '@internal/backstage-plugin-leanix';

import {
  EntityBitriseContent,
  isBitriseAvailable,
} from '@backstage-community/plugin-bitrise';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';

import {
  EntityJiraOverviewCard,
  isJiraAvailable,
} from '@ncgt/backstage-plugin-jira';

import {
  EntityCircleCIContent,
  isCircleCIAvailable,
} from '@ncgt/backstage-circleci-plugin';

import { EntityKubernetesContent } from '@backstage/plugin-kubernetes';

import {
  NewRelicContent,
  isNewRelicAvailable,
} from '../../plugins/dashboard-new-relic/src';

import { EntityQRCode } from './EntityQRCode/EntityQRCode';
import { EntityCatalogGraphCardCustom } from './EntityCatalogGraphCard/EntityCatalogGraphCard';
import { CustomEntityAboutCard } from './AboutCard';
import {
  EntityAwsCodePipelineCard,
  EntityAwsCodePipelineExecutionsContent,
  isAwsCodePipelineAvailable,
} from '@aws/aws-codepipeline-plugin-for-backstage';
import { ReadmeCard } from '@ncgt/backstage-plugin-readme';
import { ProductivityHubPage } from '@internal/backstage-plugin-productivity-hub';
import { BadgesCard } from '@internal/backstage-plugin-badges';
import { AnalyticsDbPage } from '@internal/backstage-plugin-analytics-db';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  isNCUDeploymentDashboardAnnotationPresent,
  DeploymentDashboard,
} from '@internal/backstage-plugin-deployment-dashboard';

const doesEntityHasAnnotation =
  (annotation: string): Function =>
  (entity: Entity): boolean => {
    if (entity.metadata?.annotations) {
      return Boolean(entity.metadata?.annotations[annotation]);
    }
    return false;
  };

const isCxOneFeatureEnabled = (): boolean => {
  const config = useApi(configApiRef);
  const enabled = config.getOptionalBoolean('cxone.feature_enabled') ?? true;
  return enabled;
};

const isEntityComingSoon = (entity: Entity): boolean => {
  return entity.spec?.lifecycle === 'coming-soon';
};

const isOwner = (entity: Entity): boolean => {
  // eslint-disable-next-line prefer-const
  let [fallback, entityOwner] = ((entity.spec?.owner ?? '') as string).split(
    ':',
  );
  entityOwner = entityOwner ?? fallback;
  const identityUser = localStorage.getItem('userIdentity');
  return entityOwner === identityUser;
};

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const awsMiniCostCenterContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isAWSCostAnnotationPresent}>
      <AwsMiniCostCenterPage />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No Cost Tag information available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable the miniCost Center for it."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const ncuDeploymentDashboardContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isNCUDeploymentDashboardAnnotationPresent}>
      <DeploymentDashboard />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No Deployment Dashboard information available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable the the Deployment Dashboard for it."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const awsS3ViewerContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isAWSS3ViewerAnnotationPresent}>
      <AwsS3BucketViewerPage />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No S3 Viewer information available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable the S3 Viewer for it."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const circleciContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isCircleCIAvailable}>
      <EntityCircleCIContent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No CircleCI information available for this entity"
        missing="info"
        description={
          'Please verify that the annotation is added to the entity and that the configuration is correct.' +
          'if you believe this is an error on Backstage, please report an issue.'
        }
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://github.com/newscorp-ghfb/global-backstage/issues"
          >
            Report an issue
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

// if both aws codebuild and aws codepipeline are available, show both
const awsCicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case
      if={entity =>
        isAwsCodeBuildAvailable(entity) && isAwsCodePipelineAvailable(entity)
      }
    >
      <EntityAwsCodeBuildCard />
      <br />
      <EntityAwsCodePipelineExecutionsContent />
      <br />
      <EntityAwsCodePipelineCard />
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isAwsCodeBuildAvailable}>
      <EntityAwsCodeBuildCard />
    </EntitySwitch.Case>
    <EntitySwitch.Case if={isAwsCodePipelineAvailable}>
      <EntityAwsCodePipelineExecutionsContent />
      <br />
      <EntityAwsCodePipelineCard />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No AWS CICD information available for this entity"
        missing="info"
        description={
          'Please verify that the annotation is added to the entity and that the configuration is correct.' +
          'if you believe this is an error on Backstage, please report an issue.'
        }
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://github.com/newscorp-ghfb/global-backstage/issues"
          >
            Report an issue
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const teamStatisticsContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={12}>
      <TeamStructureCard />
    </Grid>
    <Grid item md={12}>
      <TeamInfoCard />
    </Grid>
  </Grid>
);

const cxoneContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={12}>
      <CXOneCard />
    </Grid>
  </Grid>
);
const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item>
      <AnalyticsDbPage />
    </Grid>
    <Grid item md={6}>
      <CustomEntityAboutCard variant="gridItem" />
    </Grid>
    <Grid container item md={6}>
      <EntityQRCode />
      <Grid item xs={12}>
        <EntityCatalogGraphCardCustom />
      </Grid>
    </Grid>
    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <ComponentStatsCard />
    </Grid>
    <Grid item md={6} xs={12}>
      <BadgesCard />
    </Grid>
    {entityWarningContent}
  </Grid>
);

const ServiceEntityPage = () => {
  const config = useApi(configApiRef);
  const isCxOneFeatureEnabled =
    config.getOptionalBoolean('cxone.feature_enabled') ?? true;

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/readme" title="Readme">
        <ReadmeCard variant="fullHeight" />
      </EntityLayout.Route>

      <EntityLayout.Route
        if={entity =>
          isAwsCodeBuildAvailable(entity) || isAwsCodePipelineAvailable(entity)
        }
        path="/aws-cicd"
        title="AWS CI/CD"
      >
        {awsCicdContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        if={isCircleCIAvailable}
        path="/circleci"
        title="CircleCI"
      >
        {circleciContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/api" title="API">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <EntityProvidedApisCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumedApisCard />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/dependencies" title="Dependencies">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <EntityDependsOnComponentsCard variant="gridItem" />
          </Grid>
          <Grid item md={6}>
            <EntityDependsOnResourcesCard variant="gridItem" />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/docs"
        title="Docs"
        if={entity =>
          doesEntityHasAnnotation('backstage.io/techdocs-ref')(entity)
        }
      >
        {techdocsContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/kubernetes"
        title="Kubernetes"
        if={entity =>
          doesEntityHasAnnotation('backstage.io/kubernetes-label-selector')(
            entity,
          )
        }
      >
        <EntityKubernetesContent refreshIntervalMs={30000} />
      </EntityLayout.Route>

      <EntityLayout.Route
        if={isNewRelicAvailable}
        path="/newrelic"
        title="New Relic Dashboard"
      >
        <NewRelicContent />
      </EntityLayout.Route>

      <EntityLayout.Route path="/team" title="Team">
        {teamStatisticsContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        if={entity =>
          doesEntityHasAnnotation('ghfb/cxone')(entity) && isCxOneFeatureEnabled
        }
        path="/cxone"
        title="CXOne"
      >
        {cxoneContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/metrics"
        title="Metrics"
        if={entity =>
          doesEntityHasAnnotation(
            'backstage.io/productivity-hub-metrics-enabled',
          )(entity)
        }
      >
        <ProductivityHubPage />
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/deployment-dashboard"
        title="Deployment Dashboard"
        if={isNCUDeploymentDashboardAnnotationPresent}
      >
        {ncuDeploymentDashboardContent}
      </EntityLayout.Route>
    </EntityLayout>
  );
};

const WebsiteEntityPage = () => {
  const config = useApi(configApiRef);
  const isCxOneFeatureEnabled =
    config.getOptionalBoolean('cxone.feature_enabled') ?? true;

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        {overviewContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/readme" title="Readme">
        <ReadmeCard variant="fullHeight" />
      </EntityLayout.Route>

      <EntityLayout.Route
        if={entity =>
          isAwsCodeBuildAvailable(entity) || isAwsCodePipelineAvailable(entity)
        }
        path="/aws-cicd"
        title="AWS CI/CD"
      >
        {awsCicdContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        if={isCircleCIAvailable}
        path="/circleci"
        title="CircleCI"
      >
        {circleciContent}
      </EntityLayout.Route>

      <EntityLayout.Route path="/aws-mini-cost-center" title="Cost Center">
        {awsMiniCostCenterContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/aws-s3-bucket-viewer"
        title="S3"
        if={entity =>
          doesEntityHasAnnotation('newscorp.com/aws-s3-bucket-viewer')(entity)
        }
      >
        {awsS3ViewerContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/kubernetes"
        title="Kubernetes"
        if={entity =>
          doesEntityHasAnnotation('backstage.io/kubernetes-label-selector')(
            entity,
          )
        }
      >
        <EntityKubernetesContent refreshIntervalMs={30000} />
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/jira"
        if={entity =>
          (!isEntityComingSoon(entity) || isOwner(entity)) &&
          isJiraAvailable(entity)
        }
        title="JIRA"
      >
        <EntityJiraOverviewCard />
      </EntityLayout.Route>

      <EntityLayout.Route path="/dependencies" title="Dependencies">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={6}>
            <EntityDependsOnComponentsCard variant="gridItem" />
          </Grid>
          <Grid item md={6}>
            <EntityDependsOnResourcesCard variant="gridItem" />
          </Grid>
        </Grid>
      </EntityLayout.Route>

      <EntityLayout.Route path="/team" title="Team">
        {teamStatisticsContent}
      </EntityLayout.Route>
      <EntityLayout.Route
        if={entity =>
          doesEntityHasAnnotation('ghfb/cxone')(entity) && isCxOneFeatureEnabled
        }
        path="/cxone"
        title="CXOne"
      >
        {cxoneContent}
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/deployment-dashboard"
        title="Deployment Dashboard"
        if={isNCUDeploymentDashboardAnnotationPresent}
      >
        {ncuDeploymentDashboardContent}
      </EntityLayout.Route>
    </EntityLayout>
  );
};

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */
const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/readme" title="Readme">
      <ReadmeCard variant="fullHeight" />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={entity =>
        isAwsCodeBuildAvailable(entity) || isAwsCodePipelineAvailable(entity)
      }
      path="/aws-cicd"
      title="AWS CI/CD"
    >
      {awsCicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      if={isCircleCIAvailable}
      path="/circleci"
      title="CircleCI"
    >
      {circleciContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/bitrise"
      if={entity =>
        (!isEntityComingSoon(entity) || isOwner(entity)) &&
        isBitriseAvailable(entity)
      }
      title="Bitrise"
    >
      <EntityBitriseContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/jira"
      if={entity =>
        (!isEntityComingSoon(entity) || isOwner(entity)) &&
        isJiraAvailable(entity)
      }
      title="JIRA"
    >
      <EntityJiraOverviewCard />
    </EntityLayout.Route>

    <EntityLayout.Route
      if={entity =>
        doesEntityHasAnnotation('newscorp.com/aws-s3-bucket-viewer')(entity)
      }
      path="/aws-s3-bucket-viewer"
      title="S3"
    >
      {awsS3ViewerContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/newrelic"
      if={entity =>
        (!isEntityComingSoon(entity) || isOwner(entity)) &&
        isNewRelicAvailable(entity)
      }
      title="New Relic"
    >
      <NewRelicContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/docs"
      if={entity =>
        (!isEntityComingSoon(entity) || isOwner(entity)) &&
        doesEntityHasAnnotation('backstage.io/techdocs-ref')(entity)
      }
      title="Docs"
    >
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/team" title="Team">
      {teamStatisticsContent}
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/cxone"
      title="CXOne"
      if={entity =>
        doesEntityHasAnnotation('backstage.io/techdocs-ref')(entity) &&
        isCxOneFeatureEnabled()
      }
    >
      {cxoneContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/deployment-dashboard"
      title="Deployment Dashboard"
      if={isNCUDeploymentDashboardAnnotationPresent}
    >
      {ncuDeploymentDashboardContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case
      if={entity =>
        (!isEntityComingSoon(entity) || isOwner(entity)) &&
        isComponentType('service')(entity)
      }
    >
      <ServiceEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case
      if={entity =>
        (!isEntityComingSoon(entity) || isOwner(entity)) &&
        isComponentType('website')(entity)
      }
    >
      <WebsiteEntityPage />
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <CustomEntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <BadgesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12}>
          <EntityMembersListCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <CustomEntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <CustomEntityAboutCard variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
